import React, {useEffect, useRef, useState} from 'react';
import AsyncSelect from 'react-select/async';
import {GroupBase} from 'react-select';
import useLocalStorage from '@rehooks/local-storage';
import {
  customStyles,
  GroupedOption,
  initialGroupOptions,
  RecentOption,
  SearchOptions,
} from './SelectParts';
import Group from './SelectParts/Group';
import {Option} from './SelectParts/Option';
import {RecentSearchMenu} from './SelectParts/Menu';
import {
  ClearIndicator,
  DropdownIndicator,
  IndicatorsContainer,
  MultiValueRemove,
  ValueContainer,
} from './SelectParts/ValueContainer';
import {useLocation} from 'react-router-dom';
import useGlobalSearch from '../../hooks/useGlobalSearch';
import {usePlaceTypes} from '../../hooks/usePlaceTypes';
import {GlobalSearchParams} from '../../utils/GlobalSearch/types';
import {CONTENT_TYPE} from '../../utils/GlobalSearch/enums';

export const RECENT_SEARCH = 'recent_search';
export const FILTER_MENU_ID = 'filter-menu-title';
export const MAX_RECENT_SEARCHES = 3;

declare module 'react-select/dist/declarations/src/Select' {
  export interface Props<Option, IsMulti extends boolean, Group extends GroupBase<Option>> {
    onSubmit?: (event: React.MouseEvent<HTMLElement>) => void;
    setParams?: (params: GlobalSearchParams, options: RecentOption) => void;
    onClear?: (event: React.MouseEvent<HTMLElement>) => void;
    toggleMenu?: React.Dispatch<React.SetStateAction<boolean>>;
    inputCleared?: boolean;
  }
}

const GlobalSearch = () => {
  const {
    type,
    setType,
    searchTerm: globalSearchTerm,
    performSearch,
    params,
    filters,
    setFilters,
    resetTypeSpecificFilters,
    setTypeSpecificFilter,
  } = useGlobalSearch();

  const [menuIsOpen, setMenuIsOpen] = useState(false);
  const [inputCleared, setInputCleared] = useState(true);
  const [searchTerm, setSearchTerm] = useState<string>(globalSearchTerm || '');
  const [recentSearch, setRecentSearch] = useLocalStorage<RecentOption[]>(RECENT_SEARCH);
  const [filterGroupedOptions, setFilterGroupedOptions] =
    useState<GroupedOption[]>(initialGroupOptions);
  const [recentGroupedOptions] = useState<GroupedOption[]>([{options: recentSearch ?? []}]);
  const {pathname} = useLocation();
  useState<RecentOption | null>();
  const selectRef = useRef<any>(null);
  const {categories, placeTypesLoading} = usePlaceTypes();
  const isFeaturedContent = pathname.includes('featured-content');

  useEffect(() => {
    setSearchTerm(globalSearchTerm || '');
  }, [globalSearchTerm]);

  useEffect(() => {
    if (isFeaturedContent) {
      setSearchTerm('');
      setFilterGroupedOptions(initialGroupOptions);
      setInputCleared(true);
    }
  }, [isFeaturedContent]);

  const handleInputChange = (inputValue, actionMeta) => {
    if (actionMeta.action === 'input-change') {
      setSearchTerm(inputValue);
    }
  };

  const handleFocus = (e) => {
    setMenuIsOpen(true);
  };

  const handleSearchSubmit = () => {
    performSearch(searchTerm);

    // Save search to recent searches if there's a search term
    if (searchTerm && searchTerm.trim() !== '') {
      // Use the actual search params from useGlobalSearch with current search term
      const searchParams: GlobalSearchParams = {
        ...params,
        term: searchTerm.trim(),
        type: type,
        filters: filters?.[type] || {},
      };

      const newRecentSearch: RecentOption = {
        term: searchTerm.trim(),
        type: type,
        params: searchParams,
        // Save current filters for this content type
        ...(filters?.[type] &&
          Object.keys(filters[type] || {}).length > 0 && {
            value: JSON.stringify(filters[type]),
            label: `${searchTerm.trim()} (${Object.keys(filters[type] || {}).length} filters)`,
          }),
      };

      const currentRecentSearches = recentSearch || [];
      // Remove duplicate if it exists
      const filteredSearches = currentRecentSearches.filter(
        (search) => search.term !== searchTerm.trim()
      );
      // Add new search to the beginning and limit to MAX_RECENT_SEARCHES recent searches
      const updatedRecentSearches = [newRecentSearch, ...filteredSearches].slice(0, MAX_RECENT_SEARCHES);
      setRecentSearch(updatedRecentSearches);
    }
  };

  const handleSearchInit = (e) => {
    e.preventDefault();
    setMenuIsOpen(false);
    handleSearchSubmit();
    // Blur the input to close the keyboard on mobile devices
    if (selectRef.current?.inputRef) {
      selectRef.current.inputRef.blur();
    }
  };

  const handleClear = (e: React.MouseEvent<HTMLElement>) => {
    e.preventDefault();
    const element = e.target as HTMLElement;
    setSearchTerm('');
    selectRef.current.inputRef.focus();
    setFilterGroupedOptions(initialGroupOptions);

    // setSelectedOption(initialSelectedOption);
    setInputCleared(true);
    if (
      element.parentElement?.parentElement?.id === 'home-dockbar' ||
      element.parentElement?.parentElement?.id === 'dockbar2-tooltip' ||
      element.parentElement?.parentElement?.id === 'wallet-tooltip' ||
      categories.includes(element.id)
    ) {
      selectRef.current.inputRef.blur();
      setMenuIsOpen(false);
    }
  };

  const handleOnKeyDown = (e: React.KeyboardEvent<HTMLDivElement>) => {
    // Check for Enter key in different forms (covers desktop and mobile keyboards)
    if (e.code === 'Enter' || e.key === 'Enter' || e.keyCode === 13) {
      handleSearchInit(e);
    }
  };

  const handleRecentSearchInit = (params: GlobalSearchParams, options: RecentOption) => {
    setMenuIsOpen(false);
    setInputCleared(false);
    selectRef.current.inputRef.blur();

    // Set the search term from options
    if (options?.term) {
      const searchTerm = options.term.trim();
      const targetType = (options?.type as CONTENT_TYPE) || type;
      const filters = options.params?.filters;

      setSearchTerm(searchTerm);

      // Set the type first if it's different
      if (options?.type && options?.type !== CONTENT_TYPE.ALL) {
        setType(options?.type as CONTENT_TYPE);
      }

      // Clear existing filters for the target type first
      resetTypeSpecificFilters(targetType);

      // Restore filters if they were saved with the recent search
      if (options?.params?.filters && Object.keys(options.params.filters).length > 0) {
        // Set each filter individually using setTypeSpecificFilter
        Object.keys(options.params.filters).forEach((key) => {
          if (options.params?.filters) {
            setTypeSpecificFilter(targetType, key, filters);
          }
        });
      }

      performSearch(searchTerm, targetType, filters);
    }
  };

  return (
    <>
      <AsyncSelect<SearchOptions, true, GroupedOption>
        id="search-tooltip"
        styles={customStyles}
        placeholder={'Search...'}
        isMulti
        isSearchable
        menuIsOpen={menuIsOpen}
        hideSelectedOptions={false}
        blurInputOnSelect={false}
        components={{
          DropdownIndicator: (props) => (
            <DropdownIndicator {...props} onSubmit={handleSearchInit} />
          ),
          ValueContainer,
          Menu: RecentSearchMenu,
          MultiValueRemove,
          Group,
          Option,
          IndicatorsContainer,
          ClearIndicator,
        }}
        onInputChange={handleInputChange}
        onFocus={handleFocus}
        inputValue={searchTerm ?? ''}
        defaultOptions={
          (searchTerm === null || searchTerm === '') && inputCleared
            ? recentGroupedOptions
            : filterGroupedOptions
        }
        onSubmit={handleSearchInit}
        setParams={handleRecentSearchInit}
        onClear={handleClear}
        toggleMenu={setMenuIsOpen}
        ref={selectRef}
        onKeyDown={handleOnKeyDown}
        inputCleared={inputCleared}
      />
    </>
  );
};

export default GlobalSearch;
